// All page content lives here so updates don't require touching layout code.

export const profile = {
    name: 'Luis Velez',
    title: 'Engineering Leader · Data Platforms & AI',
    pitch: 'I build data platforms, cloud applications and the teams behind them. Lately that includes LLM workflows running on local models.',
    location: 'Greater Tampa Bay Area',
    email: 'luis.israel.velez@gmail.com',
    linkedin: 'https://www.linkedin.com/in/luis-velez-56442657/',
    github: 'https://github.com/luis-israel-velez',
    about: 'Passionate leader and engineer with a proven record of building highly productive, results-oriented tech teams by identifying highly motivated people while mentoring and growing existing talent. Successfully executed legacy-to-cloud global migration projects, as well as building new applications from the ground up, with an emphasis on improving performance while minimizing cost. Solutions were built to be highly scalable and resilient to adapt to ever-growing business needs.',
    stats: [
        {
            value: '8-figure',
            label: 'Annual revenue supported',
            detail: 'by the AWS cloud data platform I lead, serving thousands of clients'
        },
        {
            value: '50+',
            label: 'Engineers led',
            detail: 'in global orgs spanning the US, Eastern Europe, South America and India'
        },
        {
            value: 'RAG & LLMs',
            label: 'Hands-on AI engineering',
            detail: 'built a cited RAG chatbot and a local-LLM summarization pipeline'
        }
    ]
}

export const experience = [
    {
        company: 'Experian',
        role: 'Senior Director of Data Platform Engineering',
        start: '03/2023',
        end: 'Present',
        groups: [
            {
                title: 'Leadership',
                points: [
                    'Built and led a 30+ person global technology organization across architecture, software, DevOps, QA, data and UI/UX, hiring and developing talent in the US, Eastern Europe, South America and India.',
                    'Partnered with peer leaders and stakeholders on cross-org cloud migrations, and founded an internal shared-component repository that reduced siloed solutions; it has been adopted by multiple value streams.'
                ]
            },
            {
                title: 'Cloud Data Platform',
                points: [
                    'Migrated mainframe and legacy applications to a new AWS Cloud Data Platform (CDP), now serving thousands of clients, processing terabytes of data and supporting upwards of 8 figures in annual revenue.',
                    'Architected and led delivery of a self-service data platform that lets users ingest new sources, auto-detect schemas and identities, and build data pipelines integrated with 6 enterprise products, delivering a single-pane-of-glass experience.',
                    'Integrated contract and billing management into the platform, creating one system for launching new client offerings and pricing models and improving time-to-market for new contracts by about 20%.'
                ]
            },
            {
                title: 'Engineering Excellence',
                points: [
                    'Established FinOps and SecOps visibility with dashboards tracking cloud spend and security posture across code and infrastructure, cutting cloud costs by 30% in some legacy areas and significantly reducing security vulnerabilities.',
                    'Drove continuous improvement of engineering performance by instrumenting delivery metrics (e.g., DORA) and adopting new tooling (e.g., BlueOptima), improving deployment frequency by 20% at the project\'s peak.'
                ]
            }
        ]
    },
    {
        company: 'CustomerX.i',
        role: 'Chief Technology Officer',
        start: '08/2021',
        end: '03/2023',
        groups: [
            {
                title: 'Web Applications',
                points: [
                    'Built multiple enterprise-level web applications on Azure cloud infrastructure and Kubernetes. These applications let users self-onboard their point-of-sale data using connectors to obtain near real-time insights through multiple dashboards.',
                    'Deployed a supporting data platform ecosystem: a Delta Lake on ADLS Gen 2 (storing terabytes of data across multiple cleaning stages), Databricks data pipelines (processing and calculating value for our customers) and an orchestration layer.',
                    'Leveraged Infrastructure as Code to maximize delivery speed for new features.'
                ]
            },
            {
                title: 'Team Building',
                points: [
                    'Hired and trained a technology team (25+) across multiple disciplines (Architecture, Software, DevOps, Quality, Data and UI/UX Engineering).',
                    'Established engineering practices to ensure quick and efficient development, including Agile and CI/CD on all repositories, code standards, checklists and automated SQA testing.',
                    'Managed application and team costs alongside other executives; built KPI and executive dashboards to track efforts across teams.'
                ]
            },
            {
                title: 'Partner & Customer Relationships',
                points: [
                    'Worked directly with customers, consulting companies and third-party application platforms to strengthen CX.i relationships and achieve business goals.',
                    'Participated in customer feedback sessions to capture the impact and value of our application and pivot accordingly.'
                ]
            }
        ]
    },
    {
        company: 'Catalina Marketing',
        role: 'Data Engineering Director',
        start: '01/2015',
        end: '07/2021',
        groups: [
            {
                title: 'Leadership',
                points: [
                    'Led data engineering teams (50+) across multiple disciplines (Architecture, ETL, DBA, Data, Quality, DevOps), spanning worldwide offices and stakeholders.',
                    'Executed the company legacy-to-cloud migration strategy, successfully moving our existing suite of tools.'
                ]
            },
            {
                title: 'Data Platform',
                points: [
                    'Responsible for a data platform handling over 300 TB and millions of transactions per day, spanning legacy and cloud technologies.',
                    'Built and managed 25+ data solutions for worldwide stakeholders, used by multiple CPGs, retailers and agencies.'
                ]
            },
            {
                title: 'Financials',
                points: [
                    'Custodian of the technology budget for data management, including multi-million dollar deals for technology licensing, resource management and support infrastructure.',
                    'Led multiple rounds of cost optimization, tuning technology for better returns and negotiating better pricing with partners.'
                ]
            }
        ]
    },
    {
        company: 'Softtek PR',
        role: 'ETL Informatica Consultant',
        start: '06/2011',
        end: '12/2014',
        groups: [
            {
                title: 'Engineering',
                points: [
                    'Maintained and supported the data warehouse for one of the largest health care companies in Puerto Rico.',
                    'Designed and developed an orchestration application adopted company-wide and used across all customer projects, built on Linux scripts, XML-based contracts, an RDBMS for audit information and Informatica for execution.',
                    'Led multiple customer projects as Solutions Architect and trained junior staff.'
                ]
            },
            {
                title: 'Customer Relationships',
                points: [
                    'Worked directly with customers in the telecommunications, banking and health industries: gathered requirements, analyzed business needs and designed data solutions built with Informatica and multiple RDBMSs.'
                ]
            }
        ]
    },
    {
        company: 'Hamilton Sundstrand',
        role: 'Software Engineer',
        start: '05/2010',
        end: '09/2010',
        groups: [
            {
                title: 'Engineering',
                points: [
                    'Built features for the ORION space capsule GUI using .NET. The GUI displayed capsule sensors and controlled basic life-support functions.',
                    'Tested fault-detection classes using emulators to stress test the system in a controlled environment.'
                ]
            }
        ]
    }
]

export const projects = [
    {
        title: 'Course Summary',
        description: 'Python toolkit that turns lecture transcripts into study materials using local LLMs served through LM Studio.',
        highlights: [
            'Three-stage pipeline: per-class summarization into markdown, a course-level rollup and a grounded chatbot that answers with citations.',
            'Automated model lifecycle management, shared output formats across stages and fail-fast parsing.',
            'Tested end-to-end on an 8-class university logic course.'
        ],
        stack: ['Python', 'LM Studio', 'Local LLMs']
    },
    {
        title: 'DDC Rule Assistance',
        description: 'Self-hosted RAG chatbot for Slack and Discord that answers Dungeon Crawler Carl tabletop RPG rules questions with source citations.',
        highlights: [
            'Ingestion with column-aware PDF parsing (PyMuPDF) feeding a ChromaDB vector store.',
            'Retrieval and generation through LM Studio endpoints, with Slack and Discord frontends sharing one core.',
            'Model evaluation comparing Llama and Qwen, plus structured logging to track latency.'
        ],
        stack: ['Python', 'RAG', 'ChromaDB', 'PyMuPDF', 'LM Studio', 'Slack', 'Discord']
    }
]

export const skills = [
    {
        category: 'Languages',
        items: ['Python', 'SQL', 'Scala', 'C#', 'C++', 'JavaScript & TypeScript', 'Shell scripting', 'HTML & CSS']
    },
    {
        category: 'AI & LLM Engineering',
        items: ['RAG pipelines', 'ChromaDB', 'LM Studio', 'Local LLMs (Llama, Qwen)', 'Model evaluation']
    },
    {
        category: 'Data Storage & Processing',
        items: ['Spark', 'Databricks', 'Snowflake', 'Cloud storage (ADLS, GCS, S3)', 'Oracle, Postgres, MySQL', 'Netezza', 'MongoDB', 'Elasticsearch', 'Kafka & Confluent']
    },
    {
        category: 'Cloud & Frameworks',
        items: ['Azure', 'GCP', 'Kubernetes', 'Node.js', 'React', 'Hadoop (Hortonworks)']
    },
    {
        category: 'DevOps',
        items: ['GitHub', 'Terraform (IaC)', 'Azure DevOps', 'Google Cloud Code', 'CI/CD']
    }
]
